import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowLeft, Phone } from 'lucide-react';
type Step = 1 | 2 | 3;
interface QuickPayLayoutProps {
  currentStep: Step;
  showSuccess?: boolean;
  onBack?: () => void;
  wide?: boolean;
  children: React.ReactNode;
}
const steps: {
  id: Step;
  label: string;
}[] = [{
  id: 1,
  label: 'Reference'
}, {
  id: 2,
  label: 'Pay'
}, {
  id: 3,
  label: 'Done'
}];
export function QuickPayLayout({
  currentStep,
  showSuccess,
  onBack,
  wide,
  children
}: QuickPayLayoutProps) {
  const mainWidth = wide ? 'max-w-5xl' : 'max-w-2xl';
  return <div className="min-h-screen bg-brand-gray flex flex-col font-sans" data-id="element-812">
      {/* Slim Header */}
      <header className="bg-white border-b border-gray-200" data-id="element-813">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-814">
          <Link to="/portal" className="flex items-center" aria-label="Back to portal home" data-id="element-815">
            <img src="https://cdn.magicpatterns.com/uploads/miiyVjkag3qKFYCybFaUjL/pasted-image.jpg" alt="Compass Recovery Group, LLC" className="h-9 w-auto object-contain" data-id="element-816" />
          </Link>
          <div className="flex items-center gap-2 text-xs font-medium text-brand-muted" data-id="element-817">
            <Lock size={14} className="text-green-600" data-id="element-818" />
            <span className="hidden sm:inline" data-id="element-819">Secure encrypted session</span>
            <span className="sm:hidden" data-id="element-820">Secure</span>
          </div>
        </div>
      </header>

      {!showSuccess && <>
          {/* Agent-arranged context banner */}
          <div className="bg-brand-navy text-white" data-id="element-821">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-2 text-xs font-medium" data-id="element-822">
              <Phone size={14} data-id="element-823" />
              <span data-id="element-824">Continuing a payment arranged with your Compass agent</span>
            </div>
          </div>

          {/* Progress Stepper */}
          <div className="bg-white border-b border-gray-200" data-id="element-825">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-826">
              <ol className="flex items-center justify-center gap-2 max-w-2xl mx-auto" data-id="element-827">
                {steps.map((step, index) => {
              const isComplete = currentStep > step.id;
              const isCurrent = currentStep === step.id;
              return <li key={step.id} className="flex items-center gap-2 flex-1 min-w-0 last:flex-none" data-id="element-828">
                      <div className={`flex items-center gap-2 flex-shrink-0 ${isCurrent || isComplete ? '' : 'opacity-50'}`} data-id="element-829">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${isComplete ? 'bg-brand-navy text-white' : isCurrent ? 'bg-brand-red text-white' : 'bg-gray-200 text-brand-muted'}`} aria-current={isCurrent ? 'step' : undefined} data-id="element-830">
                          {isComplete ? '✓' : step.id}
                        </div>
                        <span className={`text-sm font-semibold hidden sm:inline ${isCurrent ? 'text-brand-navy' : 'text-brand-muted'}`} data-id="element-831">
                          {step.label}
                        </span>
                      </div>
                      {index < steps.length - 1 && <div className={`flex-1 h-px ${currentStep > step.id ? 'bg-brand-navy' : 'bg-gray-200'}`} data-id="element-832" />}
                    </li>;
            })}
              </ol>
            </div>
          </div>
        </>}

      {onBack && !showSuccess && <div className={`${mainWidth} mx-auto w-full px-4 sm:px-6 lg:px-8 pt-6`} data-id="element-833">
          <button onClick={onBack} className="inline-flex items-center gap-1 text-sm font-medium text-brand-muted hover:text-brand-navy transition-colors" data-id="element-834">
            <ArrowLeft size={16} data-id="element-835" />
            Back
          </button>
        </div>}

      <main className="flex-grow flex flex-col" data-id="element-836">
        <div className={`${mainWidth} mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 md:py-12 flex-grow`} data-id="element-837">
          {children}
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-4" data-id="element-838">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-brand-muted" data-id="element-839">
          This communication is from a debt collector. This is an attempt to
          collect a debt and any information obtained will be used for that
          purpose.
        </div>
      </footer>
    </div>;
}